export const redirectToHttps = () => {
    const domain = window.location.hostname;
    if(domain !== 'localhost'){
        let url = window.location.href;
        if(!url.includes('https')){
            url = url.replace('http', 'https');
            window.location.href = url;
        }
    }
};

/** Build-time feature flags.
 *
 * Flags are read from REACT_APP_FF_* environment variables (see the `.env`,
 * `.env.staging`, `.env.uat`, and `.env.production` files — builds select one via
 * env-cmd in package.json). CRA inlines these at build time, so changing a
 * flag requires a rebuild/redeploy.
 *
 * Flags are FAIL-CLOSED: a flag is off unless its variable is exactly 'true'.
 * An environment file that omits the variable ships with the feature hidden.
 */

/** @param {string|undefined} value - raw REACT_APP_FF_* value */
const envFlag = (value) => value === 'true';

export const FEATURES = Object.freeze({
    /** AIS integration — the "Athos XGen Payment Link" Advanced Admin page
     * (AthosXGenPaymentLinkGeneratePage). Gates its route, sidebar entry,
     * and hub tile via pathRegistry. Off until AIS is ready to launch;
     * flip `REACT_APP_FF_AIS_XGEN_PAYMENT_LINK` in the relevant `.env*` file.
     */
    AIS_XGEN_PAYMENT_LINK: envFlag(process.env.REACT_APP_FF_AIS_XGEN_PAYMENT_LINK),

    /** EGL/Intact admin pages — EGLIStateTogglePage, EGLReactRolloutPage, and
     * FormAssemblyAdminPage. Gates their route, sidebar entry, and hub tile
     * via pathRegistry. Off until the EGL/Intact program is ready to launch;
     * flip `REACT_APP_FF_EGLI_ADMIN_PAGES` in the relevant `.env*` file.
     */
    EGLI_ADMIN_PAGES: envFlag(process.env.REACT_APP_FF_EGLI_ADMIN_PAGES),
});

// Environment configuration with shared and environment-specific variables
const GlobalConfig = {
    /** API Key for the API */
    API_KEY: process.env.REACT_APP_API_KEY,
    /** Document API Key for the API */
    DOCUMENT_API_KEY: process.env.REACT_APP_DOCUMENT_API_KEY,
    APP_NAME: process.env.REACT_APP_NAME || '',
};
const EnvironmentConfigs = {
    development: {
        // Overridable so a local backend that landed on a non-default port (e.g. due to a
        // port conflict) can still be targeted without touching the hardcoded default.
        API_URL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
        ATHOS_DOT_NET_URL: 'https://staging.athosinsurance.com',
    },
    staging: {
        API_URL: 'https://athos-api-staging-d5hag9h4dwakg0c5.westus2-01.azurewebsites.net',
        ATHOS_DOT_NET_URL: 'https://staging.athosinsurance.com',
    },
    uat: {
        API_URL: 'https://athos-api-uat-g4cmhzhndzbkgwek.westus2-01.azurewebsites.net',
        ATHOS_DOT_NET_URL: 'https://athosinsurance-stp-updates-f2ctejdpheatg4c8.westus2-01.azurewebsites.net',
    },
    production: {
        API_URL: 'https://athos-api-fugmahdsbzd0anbq.westus2-01.azurewebsites.net',
        ATHOS_DOT_NET_URL: 'https://athosinsurance.com',
        GTM_ID: process.env.REACT_APP_GTM_ID,
        GA4_ID: process.env.REACT_APP_GA4_ID,
        FB_PIXEL_ID: process.env.REACT_APP_FB_PIXEL_ID,
        UET_TAG_ID: process.env.REACT_APP_UET_TAG_ID,
    },
};
const ExternalConfigs = {
    GOOGLE_PLACES_API_KEY: process.env.REACT_APP_GOOGLE_PLACES_API,
};

export const Environments = Object.freeze({
    development: 'development',
    staging: 'staging',
    uat: 'uat',
    production: 'production',
});

// Helper function to get config values
export const getConfig = (key) => config[key];

export const getEnv = () => {
    // const domain = window.location.hostname;
    // if(domain === 'localhost'){
    //     return Environments.development;
    // }
    return process.env.REACT_APP_ENV;
};
// Helper function to check if we're in a specific environment
export const isEnv = (env) => {
    const domain = window.location.hostname;
    if(domain === 'localhost'){
        return Environments.development === env;
    }
    return process.env.REACT_APP_ENV === env;
};
export const isDevelopment = () => isEnv('development');
export const isStaging = () => isEnv('staging');
export const isUat = () => isEnv('uat');
export const isProduction = () => isEnv('production'); 

/**
 * @typedef {Object} Config
 * @property {string} ENV - The environment name
 * @property {string} API_KEY - The API key
 * @property {string} DOCUMENT_API_KEY - The document API key
 * @property {string} APP_NAME - The app name
 * @property {string} API_URL - The API URL
 * @property {string} ATHOS_DOT_NET_URL - The Athos .NET URL
 * @property {string} GOOGLE_PLACES_API_KEY - The Google Places API key
 * @property {string} GTM_ID - The Google Tag Manager ID (`production` only)
 * @property {string} GA4_ID - The Google Analytics 4 ID (`production` only)
 * @property {string} FB_PIXEL_ID - The Facebook Pixel ID (`production` only)
 * @property {string} UET_TAG_ID - The Microsoft UET Tag ID (`production` only)
 */

/** The configuration object for the application
 * @type {Config}
 */
export const config = {
    // ENV: 'STAGING',
    ENV: getEnv(),
    ...GlobalConfig,
    ...EnvironmentConfigs[getEnv()],
    ...ExternalConfigs,
};